import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Col, Flex, Form, Input, Row, Skeleton, Switch, Typography } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { ActivationWarning } from 'src/app/shared/components/common/tag/ActivationWarning'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { IClientSetup } from 'src/app/shared/models/clientSetup.model'
import { addClientSetup, checkClientDuplicates, getClient } from 'src/app/shared/services/ClientSetupService'
import { hasExceptionsByClient } from 'src/app/shared/services/TransactionService'
import { getLookupValues } from 'src/app/store/masterSetup'
import { colorTextSubtle } from 'src/app/themes/primaryThemeDefault'
import DelayedClientActivation from '../add-client-setup/DelayedClientActivation'
import ACHException from './ACHException'
import ArchiveClientModal from './ArchiveClientModal'
import CheckExceptions from './CheckExceptions'
import ClientInfo from './ClientInfo'
import NoExceptionModal from './hasExceptionsToResolveModal'
import './index.scss'
import MakeClientActiveModal from './MakeClientActiveModal'
import {
  convertTime,
  fetchGlobalExceptionDefaults,
  formatDataBeforeSubmit,
  generateValidationSchema,
  mappingDataToForm,
} from './utils'
import ServiceTypeParameterValidationModal from './ServiceTypeParameterValidationModal'

const ClientSetupForm = () => {
  const params = useParams()
  const { id } = params
  const { t } = useTranslation()
  const navigate = useNavigate()
  const systemDefaultConfig = useSelector((state: any) => state?.masterSetup?.deferredConfig?.systemDefaults)
  const lookupValues = useSelector(getLookupValues)

  const [isFetched, setIsFetched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [createdDate, setCreatedDate] = useState('')

  const [hasExceptionsToResolveModalOpen, setHasExceptionsToResolveModalOpen] = useState(false)
  const [isArchiveClientModalOpen, setIsArchiveClientModalOpen] = useState(false)
  const [isMakeClientActiveModalOpen, setIsMakeClientActiveModalOpen] = useState(false)
  const [isServiceTypeParameterModalOpen, setIsServiceTypeParameterModalOpen] = useState(false)
  const [checkDuplicatedData, setCheckDuplicatedData] = useState({
    archived: null,
    clientName: '',
    clientNumber: 0,
    id: null,
    isDuplicate: false,
  })

  const [makeActiveClientName, setMakeActiveClientName] = useState('')

  const [initialValues, setInitialValues] = useState<any>({})

  const { AchAuthorizationRulesNotificationType } = lookupValues || {}

  const {
    // Check Exceptions
    defaultCheckDecision,
    defaultCheckReason,
    // ACH Exceptions
    defaultAchDecision,
    defaultAchReason,
  } = systemDefaultConfig || {}

  const [disableActivationDate, setDisableActivationDate] = useState<boolean>(false)
  const [activationDate, setActivationDate] = useState<Dayjs | null>(null)

  const [hasAchException, setHasAchException] = useState<boolean>(false);
  const [hasCheckException, setHasCheckException] = useState<boolean>(false);

  const [accountNamesWithServiceTypeParameterIssue, setAccoutNamesWithServiceTypeParameterIssue] = useState<string[]>([]);

  const getClientInfo = useCallback(async () => {
    if (id) {
      const res = await getClient(id)

      const formattedData = mappingDataToForm(res?.data)

      // These come from the global System-Wide Rules config, not the Client. The form is empty until
      // this load resolves, so the fields render directly with the global values.
      const globalExceptionDefaults = await fetchGlobalExceptionDefaults()
      if (globalExceptionDefaults) Object.assign(formattedData, globalExceptionDefaults)

      const date = convertTime({
        date: res?.data?.createdAt,
      })
      setInitialValues(formattedData)
      setCreatedDate(date)
      setIsFetched(true)
      if (formattedData?.activationDate) {
        setActivationDate(formattedData.activationDate)
        const today = dayjs()
        const isBeforeDay = today.isBefore(formattedData.activationDate, 'day')
        if (isBeforeDay) {
          setDisableActivationDate(false)
        } else {
          setDisableActivationDate(true)
        }
      } else {
        setDisableActivationDate(true)
      }
      return formattedData
    }
    navigate(menuUrls.clientSetup)
  }, [id, navigate])

  const getHasExceptions = useCallback(async () => {
    if (id) {
      const res = await hasExceptionsByClient(id);
      setHasAchException(res.data.hasAchExceptions);
      setHasCheckException(res.data.hasCheckExceptions);
    }
  }, [id]);

  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    setError,
    reset,
  } = useForm<any>({
    mode: 'all',
    // defaultValues: async () => await getClientInfo(),
    resolver: yupResolver(generateValidationSchema(t)),
  })

  const isDisplayIssuedPayee = useWatch({
    control,
    name: 'displayIssuedPayee',
  })

  const isPayeeMatchChecked = useWatch({
    control,
    name: 'payeeMatch',
  })

  const isAutoloadIssuedChecked = useWatch({
    control,
    name: 'autoLoadIssuedCheckFiles',
  })

  const defaultCheckExceptionCreationType = useWatch({
    control,
    name: 'defaultCheckExceptionCreationType',
  })

  const defaultAchExceptionCreationType = useWatch({
    control,
    name: 'defaultAchExceptionCreationType',
  })

  const isArchiveChecked = useWatch({ control, name: 'archived' })
  const isArchivedChanged = initialValues?.archived !== isArchiveChecked

  const isCreateExceptions = defaultCheckExceptionCreationType !== 'NOCREATE'
  const isCreateAchExceptions = defaultAchExceptionCreationType !== 'NOCREATE'

  useEffect(() => {
    if (!isCreateExceptions) clearErrors('issuedCheckFileFormats')

    if (!isAutoloadIssuedChecked) clearErrors('sftpFileLocation')

    if (!isPayeeMatchChecked) clearErrors('payeeMatchParameterId')
  }, [isCreateExceptions, clearErrors, isAutoloadIssuedChecked, isPayeeMatchChecked])

  const handleCancel = () => {
    navigate(menuUrls.clientSetup)
  }

  useEffect(() => {
    if (!isCreateAchExceptions && !isCreateExceptions) {
      setError('defaultCheckExceptionCreationType', {
        type: 'manual',
        message: t('At least one exception type (ACH or Check) must be enabled to proceed.'),
      })
      setError('defaultAchExceptionCreationType', {
        type: 'manual',
        message: t('At least one exception type (ACH or Check) must be enabled to proceed.'),
      })
    } else {
      clearErrors(['defaultCheckExceptionCreationType', 'defaultAchExceptionCreationType'])
    }
  })

  useEffect(() => {
    if (!isDisplayIssuedPayee) {
      setValue('payeeMatch', false)
      setValue('payeeMatchParameterId', null)
    }
    if (!isPayeeMatchChecked) {
      setValue('payeeMatchParameterId', null)
    }

    if (!isAutoloadIssuedChecked) {
      setValue('sftpFileLocation', null)
    }
  }, [isDisplayIssuedPayee, isPayeeMatchChecked, setValue, isAutoloadIssuedChecked, initialValues, getValues])

  const checkDuplicates = async (clientName: string, clientNumber: string | null) => {
    if (clientName && clientNumber) {
      const response = await checkClientDuplicates(clientName, clientNumber, id)
      return response?.data
    }
  }

  const editClient = async (data: IClientSetup, message?: string) => {
    setIsLoading(true)
    try {
      const res = await addClientSetup(formatDataBeforeSubmit(data, id, disableActivationDate))
      const { statusCode } = res
      setIsLoading(false)

      // Then show toast message if success
      if (statusCode === 'Success' && message)
        navigate(menuUrls.clientSetup, {
          state: {
            clientSetup: {
              type: 'success',
              message: message,
            },
          },
        })
    } catch (error: any) {
      setIsLoading(false)
      if(error.data.ExceptionProcessingType !== null && error.data.ExceptionProcessingType !== undefined){
        setIsServiceTypeParameterModalOpen(true)
        setAccoutNamesWithServiceTypeParameterIssue(error.data.ExceptionProcessingType)
      }
    }
    return
  }

  const [pendingSubmitData, setPendingSubmitData] = useState<IClientSetup | null>(null)

  const onSubmit = async (data: IClientSetup) => {
    if ((!isCreateExceptions && hasCheckException) || (!isCreateAchExceptions && hasAchException)) {
      setPendingSubmitData(data)
      setHasExceptionsToResolveModalOpen(true)
      return
    }

    await continueSubmit(data)
  }
  const continueSubmit = async (data: IClientSetup) => {
    // Else: Check duplicate
    const { clientName, clientNumber } = data
    setIsLoading(true)
    const response = await checkDuplicates(clientName, clientNumber)
    setIsLoading(false)
    setCheckDuplicatedData(response)

    const {
      archived,
      isDuplicate,
      clientName: duplicatedClientName,
      clientNumber: duplicatedClientNumber,
      id: duplicatedId,
    } = response
    const isActive = !archived

    // Duplicated
    if (isDuplicate) {
      // Active
      if (isActive) {
        if (getValues('clientNumber') === duplicatedClientNumber)
          setError('clientNumber', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_IS_ALREADY_ACTIVE) })
        if (getValues('clientName')?.toLowerCase() === duplicatedClientName?.toLowerCase())
          setError('clientName', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_IS_ALREADY_ACTIVE) })
        return
      }
      // Inactive
      setMakeActiveClientName(duplicatedClientName)
      // OLD LOGIC: setIsMakeClientActiveModalOpen(true)
      // NOTE: when duplicated client is inactive, system redirects to that duplicated client
      // NOTE: adjustment GBBM-83
      navigate(`${menuUrls.editClientSetup}/${duplicatedId}`)
      return
    }

    // Not duplicated

    // Make change in archive button
    if (isArchivedChanged) {
      // If archive button is checked -> Trigger archive client flow
      if (isArchiveChecked) {
        setIsArchiveClientModalOpen(true)
        return
      }
      // If archive button is unchecked -> Trigger make client active flow
      setMakeActiveClientName(getValues('clientName'))
      setIsMakeClientActiveModalOpen(true)
      return
    }

    // Make change in other fields
    editClient(
      data,
      t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_NAME_UPDATED_SUCCESSFULLY, {
        Client_Name: getValues('clientName'),
      }),
    )
  }

  // Click Yes in "NoExceptionModal"
  const handleConfirmNoExceptionModal = async () => {
    setHasExceptionsToResolveModalOpen(false)
    if (pendingSubmitData) {
      await continueSubmit(pendingSubmitData)
      setPendingSubmitData(null)
    }
  }

  // Click Yes in "MakeClientActiveModal"
  const handleConfirmMakeClientActiveModal = async () => {
    // Duplicate and inactive
    if (checkDuplicatedData?.isDuplicate && checkDuplicatedData?.archived) {
      onCloseMakeClientActiveModal()
      setIsArchiveClientModalOpen(true)
      return
    }

    // Not duplicate
    const formData = getValues()
    await editClient(
      formData,
      t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_NAME_ACTIVE, {
        Client_Name: getValues('clientName'),
      }),
    )
    onCloseMakeClientActiveModal()
  }

  // Click Yes in "ArchiveClientModal"
  const handleConfirmArchiveClientModal = async () => {
    // Duplicate and inactive
    if (checkDuplicatedData?.isDuplicate && checkDuplicatedData?.archived) {
      setValue('id', checkDuplicatedData?.id)
      setValue('archived', true)
      const formData = getValues()
      await editClient(formData)
      setIsArchiveClientModalOpen(false)
      const res = await getClientInfo()
      reset(res)

      // Re-check duplicate to display inline message error
      const response = await checkDuplicates(getValues('clientName'), getValues('clientNumber'))
      setCheckDuplicatedData(response)

      const { archived, isDuplicate, clientName: duplicatedClientName, clientNumber: duplicatedClientNumber } = response
      const isActive = !archived

      // Duplicated
      if (isDuplicate && isActive) {
        // Active
        if (getValues('clientNumber') === duplicatedClientNumber)
          setError('clientNumber', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_IS_ALREADY_ACTIVE) })
        if (getValues('clientName')?.toLowerCase() === duplicatedClientName?.toLowerCase())
          setError('clientName', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_IS_ALREADY_ACTIVE) })
        return
      }
      return
    }


    const formData = getValues()
    await editClient(
      formData,
      t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_NAME_ARICHIVE, {
        Client_Name: getValues('clientName'),
      }),
    )
    setIsArchiveClientModalOpen(false)
  }

  const onCloseArchiveClientModal = async () => {
    // Duplicate and inactive
    if (checkDuplicatedData?.isDuplicate && checkDuplicatedData?.archived) {
      setValue('id', checkDuplicatedData?.id)
      setValue('archived', false)
      const formData = getValues()
      await editClient(formData)
      setIsArchiveClientModalOpen(false)
      const res = await getClientInfo()
      reset(res)

      // Re-check duplicate to display inline message error
      const response = await checkDuplicates(getValues('clientName'), getValues('clientNumber'))
      setCheckDuplicatedData(response)

      const { archived, isDuplicate, clientName: duplicatedClientName, clientNumber: duplicatedClientNumber } = response
      const isActive = !archived

      // Duplicated
      if (isDuplicate && isActive) {
        // Active
        if (getValues('clientNumber') === duplicatedClientNumber)
          setError('clientNumber', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_IS_ALREADY_ACTIVE) })
        if (getValues('clientName')?.toLowerCase() === duplicatedClientName?.toLowerCase())
          setError('clientName', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CLIENT_IS_ALREADY_ACTIVE) })
        return
      }
      return
    }
    setIsArchiveClientModalOpen(false)
  }

  const onCloseHasExceptionsToResolveModal = () => {
    setHasExceptionsToResolveModalOpen(false)
    setPendingSubmitData(null)
  }

  const onCloseMakeClientActiveModal = () => {
    setIsMakeClientActiveModalOpen(false)
  }
  const handleConfirmAndCloseServiceTypeParameterModal = () => {
    setIsServiceTypeParameterModalOpen(false)
  }

  const isFormDataInvalid =
    !!Object.keys(errors)?.length ||
    (isPayeeMatchChecked && !getValues('payeeMatchParameterId')) ||
    (isAutoloadIssuedChecked && !getValues('sftpFileLocation'))

  const isSaveButtonDisabled = Object.keys(dirtyFields).length === 0 || isFormDataInvalid

  useEffect(() => {
    if (!isCreateExceptions) {
      clearErrors('issuedCheckFileFormats')

      setValue('defaultCheckDecision', defaultCheckDecision)
      setValue('defaultCheckReason', defaultCheckReason)
      setValue('issuedCheckFileFormats', [])
      setValue('displayIssuedPayee', true)
      setValue('payeeMatch', false)
      setValue('displayNotes', false)
      setValue('autoLoadIssuedCheckFiles', false)
      setValue('sftpFileLocation', '')
    } else {
      setValue('defaultCheckExceptionCutoffTime', initialValues?.defaultCheckExceptionCutoffTime)
    }
  }, [isCreateExceptions, clearErrors, setValue, defaultCheckDecision, defaultCheckReason, initialValues])

  useEffect(() => {
    if (!isCreateAchExceptions) {
      setValue('defaultAchDecision', defaultAchDecision)
      setValue('defaultAchReason', defaultAchReason)
      setValue(
        'achAuthorizationRulesNotificationType',
        AchAuthorizationRulesNotificationType?.find((item: any) => item?.default)?.value,
      )
    } else {
      setValue('defaultAchExceptionCutoffTime', initialValues?.defaultAchExceptionCutoffTime)
    }
  }, [
    isCreateAchExceptions,
    setValue,
    defaultAchDecision,
    defaultAchReason,
    AchAuthorizationRulesNotificationType,
    initialValues,
  ])

  useEffect(() => {
    ; (async () => {
      setIsFetched(false)
      const res = await getClientInfo()
      await getHasExceptions();
      reset(res)
    })()
  }, [id, reset, getClientInfo, getHasExceptions])

  return (
    <Form
      name='client-setup-form'
      className='edit-client-form-container'
      layout='vertical'
      onFinish={handleSubmit(onSubmit)}
    >
      {isFetched ? (
        <>
          {activationDate && !disableActivationDate && <ActivationWarning activationDate={activationDate} />}
          <ClientInfo
            control={control}
            errors={errors}
            getValues={getValues}
            displayDuplicateText={!isMakeClientActiveModalOpen}
          />

          <CheckExceptions
            control={control}
            isDisplayIssuedPayee={isDisplayIssuedPayee}
            isCreateExceptions={isCreateExceptions}
            isPayeeMatchChecked={isPayeeMatchChecked}
            autoLoadIssuedCheckFiles={isAutoloadIssuedChecked}
            errors={errors}
            setValue={setValue}
            dirtyFields={dirtyFields}
            initialValues={initialValues}
          />

          <ACHException
            control={control}
            isCreateAchExceptions={isCreateAchExceptions}
            errors={errors}
          />
          <DelayedClientActivation control={control} disabled={disableActivationDate} />
          <Row gutter={24} align='middle'>
            <Col span={12}>
              <Form.Item>
                <Controller
                  name='archived'
                  control={control}
                  render={({ field }) => (
                    <Flex gap={12}>
                      <Switch
                        {...field}
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                        onChange={(checked) => {
                          field.onChange(checked)
                          if (!checked && !disableActivationDate) {
                            setValue('activationDate', null)
                          }
                        }}
                      />
                      <Typography style={{ color: colorTextSubtle }}>
                        {t('ClientSetup:addNewClient.label.archiveClient')}
                      </Typography>
                    </Flex>
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label='Date Created'>
                <Input value={createdDate} disabled />
              </Form.Item>
            </Col>
          </Row>
          <Flex className='button-group' justify='flex-end' gap={24}>
            <Button className='cancel-button' onClick={handleCancel}>
              {t('OverallFunctionality:back_btn')}
            </Button>

            <Button
              className='save-button'
              type='primary'
              disabled={isSaveButtonDisabled}
              htmlType='button'
              loading={isLoading}
              onClick={handleSubmit(onSubmit)}
            >
              {t('OverallFunctionality:save_changes_btn')}
            </Button>
          </Flex>

          <NoExceptionModal
            onConfirm={handleConfirmNoExceptionModal}
            onClose={onCloseHasExceptionsToResolveModal}
            isOpen={hasExceptionsToResolveModalOpen}
          />

          <MakeClientActiveModal
            clientName={makeActiveClientName}
            onConfirm={handleConfirmMakeClientActiveModal}
            onClose={onCloseMakeClientActiveModal}
            isOpen={isMakeClientActiveModalOpen}
            isLoading={isLoading}
          />

          <ArchiveClientModal
            clientName={initialValues.clientName}
            onConfirm={handleConfirmArchiveClientModal}
            onClose={onCloseArchiveClientModal}
            isOpen={isArchiveClientModalOpen}
            isLoading={isLoading}
          />

          <ServiceTypeParameterValidationModal
            accountNames={accountNamesWithServiceTypeParameterIssue}
            onConfirm={handleConfirmAndCloseServiceTypeParameterModal}
            onClose={handleConfirmAndCloseServiceTypeParameterModal}
            isOpen={isServiceTypeParameterModalOpen}
            isLoading={isLoading}
          />
        </>
      ) : (
        <Skeleton />
      )}
    </Form>
  )
}

export default ClientSetupForm
